import { useEffect, useMemo, useState } from 'react'
import { Bell, KeyRound, Monitor, ShieldCheck } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { useAuth } from '@/providers/AuthProvider'
import { getIdentity } from '@/lib/auth'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { cn } from '@/lib/cn'
import { fetchAdminSecurity, enroll2FA, verify2FA, disable2FA } from '@/lib/adapters'

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-1 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-primary">
        {icon}
        {title}
      </div>
      {children}
    </div>
  )
}

/**
 * A28.02 — TOTP two-factor. Live: GET /admin/account/security + enroll/verify/
 * disable. Enroll returns the secret (shown to add to an authenticator app);
 * enabling only happens after a valid code, and disabling needs a current code.
 */
function TwoFactorSection() {
  const t = useT()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [secret, setSecret] = useState<string | null>(null) // set while enrolling
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctl = new AbortController()
    fetchAdminSecurity(ctl.signal)
      .then((s) => setEnabled(s.two_factor_enabled))
      .catch(() => setEnabled(false))
    return () => ctl.abort()
  }, [])

  async function startEnroll() {
    setBusy(true)
    setError(null)
    try {
      const { secret: sec } = await enroll2FA()
      setSecret(sec)
      setCode('')
    } catch {
      setError(t.account.backendPending)
    } finally {
      setBusy(false)
    }
  }

  async function confirmEnable() {
    setBusy(true)
    setError(null)
    try {
      await verify2FA(code.trim())
      setEnabled(true)
      setSecret(null)
      setCode('')
    } catch {
      setError(t.auth.twoFactorInvalid)
    } finally {
      setBusy(false)
    }
  }

  async function turnOff() {
    setBusy(true)
    setError(null)
    try {
      await disable2FA(code.trim())
      setEnabled(false)
      setSecret(null)
      setCode('')
    } catch {
      setError(t.auth.twoFactorInvalid)
    } finally {
      setBusy(false)
    }
  }

  const codeInput = (
    <input
      value={code}
      inputMode="numeric"
      maxLength={6}
      placeholder="000000"
      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      className="w-28 rounded-md border border-border-default bg-surface-1 px-2 py-1 text-center font-mono text-sm tracking-widest text-primary"
    />
  )
  const btn = 'rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50'

  return (
    <Section icon={<ShieldCheck size={15} className="text-accent" />} title={t.account.sec2fa}>
      {enabled === null ? (
        <span className="text-xs text-tertiary">…</span>
      ) : enabled ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <StatusBadge status="active" label={t.account.sec2faOn} />
          </div>
          <div className="flex items-center gap-2">
            {codeInput}
            <button onClick={turnOff} disabled={busy || code.length !== 6} className={cn(btn, 'bg-danger-subtle text-danger')}>
              {t.account.sec2faDisable}
            </button>
          </div>
        </div>
      ) : secret ? (
        <div className="space-y-2">
          <p className="text-xs text-secondary">{t.account.sec2faScan}</p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-tertiary">{t.account.sec2faSecret}:</span>
            <code className="select-all break-all rounded bg-surface-2 px-2 py-1 font-mono text-xs text-primary">{secret}</code>
          </div>
          <div className="flex items-center gap-2">
            {codeInput}
            <button onClick={confirmEnable} disabled={busy || code.length !== 6} className={cn(btn, 'bg-accent text-white')}>
              {t.account.sec2faVerify}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <StatusBadge status="expired" label={t.account.sec2faOff} />
          <button onClick={startEnroll} disabled={busy} className={cn(btn, 'bg-accent text-white')}>
            {t.account.sec2faEnable}
          </button>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </Section>
  )
}

/**
 * A28.02 — Xavfsizlik va kirish-huquqlari. Klient-tomon: joriy admin identity +
 * RBAC RUXSATLARI (`can()` bo'yicha nima ochiq). 2FA sozlash va qurilma/sessiya
 * boshqaruvi SERVER kutadi (admin self-2FA + session store YO'Q = DEPS lane A) —
 * o'sha bo'limlar HALOL «backend kutadi» deб belgilanadi.
 */
export function MySecurity() {
  const t = useT()
  const { can } = useAuth()
  const id = getIdentity()
  const username = id?.username ?? '—'
  const role = id?.role ?? '—'
  const roles: string[] = id?.roles ?? []

  const access = useMemo(() => {
    const groups = new Map<string, { perm: string; granted: boolean }[]>()
    for (const value of Object.values(PERMISSIONS)) {
      const area = value.split('.')[0] ?? value
      const list = groups.get(area) ?? []
      list.push({ perm: value, granted: can(value) })
      groups.set(area, list)
    }
    return [...groups.entries()].map(([area, perms]) => ({
      area,
      perms,
      granted: perms.filter((p) => p.granted).length,
    }))
  }, [can])

  const totalGranted = access.reduce((s, g) => s + g.granted, 0)
  const total = Object.keys(PERMISSIONS).length

  return (
    <>
      <PageHeader title={t.account.secTitle} screenId="A28.02" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-2xl space-y-4">
          <Section icon={<ShieldCheck size={15} className="text-accent" />} title={t.account.secIdentity}>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-secondary">{t.account.username}</span><span className="font-mono text-primary">{username}</span></div>
              <div className="flex justify-between"><span className="text-secondary">{t.account.role}</span><span className="text-primary">{role}</span></div>
              {roles.length > 0 && (
                <div className="flex flex-wrap justify-end gap-1 pt-1">
                  {roles.map((r) => <span key={r} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-secondary">{r}</span>)}
                </div>
              )}
            </div>
          </Section>

          <Section icon={<KeyRound size={15} className="text-accent" />} title={`${t.account.secAccess} · ${totalGranted}/${total}`}>
            <div className="space-y-2">
              {access.map((g) => (
                <div key={g.area}>
                  <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-tertiary">
                    {g.area}
                    <span className="text-secondary">{g.granted}/{g.perms.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {g.perms.map((p) => (
                      <span
                        key={p.perm}
                        className={cn(
                          'rounded px-1.5 py-0.5 font-mono text-[11px]',
                          p.granted ? 'bg-accent-subtle text-accent' : 'bg-surface-2 text-tertiary line-through',
                        )}
                        title={p.perm}
                      >
                        {p.perm.split('.').slice(1).join('.')}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <TwoFactorSection />


          <Section icon={<Monitor size={15} className="text-tertiary" />} title={t.account.secDevices}>
            <div className="flex items-center gap-2">
              <StatusBadge status="pending" label={t.account.backendPending} />
              <span className="text-xs text-tertiary">{t.account.secDevicesNote}</span>
            </div>
          </Section>
        </div>
      </div>
    </>
  )
}

/* ───────────────────────── A28.03 ───────────────────────── */

const PREFS = ['desktop', 'sound', 'reports', 'tickets', 'fourEyes'] as const
type Pref = (typeof PREFS)[number]
const STORE_KEY = 'grom-admin-notif-prefs'

function loadPrefs(): Record<Pref, boolean> {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (raw) return { ...defaults(), ...(JSON.parse(raw) as Partial<Record<Pref, boolean>>) }
  } catch {
    /* ignore */
  }
  return defaults()
}
function defaults(): Record<Pref, boolean> {
  return { desktop: false, sound: true, reports: true, tickets: true, fourEyes: true }
}

/**
 * A28.03 — Bildirishnoma sozlamalari. Admin-panelning O'Z (klient-tomon) bildirishnoma
 * afzalliklari — localStorage'да saqlanadi (A28.04 interfeys-sozlamalari kabi). Server
 * kerak emas: bular shu brauzerдаги UI-toast/tovush afzalliklari.
 */
export function NotificationSettings() {
  const t = useT()
  const [prefs, setPrefs] = useState<Record<Pref, boolean>>(loadPrefs)

  const toggle = (key: Pref) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(next))
      } catch {
        /* ignore */
      }
      // Asking for browser permission when desktop notifications are enabled.
      if (key === 'desktop' && next.desktop && 'Notification' in window && Notification.permission === 'default') {
        void Notification.requestPermission()
      }
      return next
    })
  }

  const label: Record<Pref, string> = {
    desktop: t.account.notifDesktop,
    sound: t.account.notifSound,
    reports: t.account.notifReports,
    tickets: t.account.notifTickets,
    fourEyes: t.account.notifFourEyes,
  }

  return (
    <>
      <PageHeader title={t.account.notifTitle} screenId="A28.03" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-xl space-y-2">
          <p className="flex items-center gap-2 pb-1 text-xs text-tertiary">
            <Bell size={13} />
            {t.account.notifNote}
          </p>
          {PREFS.map((key) => (
            <button
              key={key}
              onClick={() => toggle(key)}
              className="flex w-full items-center justify-between rounded-lg border border-border-default bg-surface-1 px-4 py-3 text-left text-sm hover:bg-surface-2"
            >
              <span className="text-primary">{label[key]}</span>
              <span
                className={cn(
                  'relative h-5 w-9 rounded-full transition-colors',
                  prefs[key] ? 'bg-accent' : 'bg-surface-3',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                    prefs[key] ? 'translate-x-4' : 'translate-x-0.5',
                  )}
                />
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  )
}
